import { Component } from 'react';
import bcrypt from 'bcryptjs';
import sharedStyles from '../single-page.module.scss';
import styles from './bcrypt-generator.component.module.scss';
import { genStyles } from '../converter-layout/generator-layout';

interface State {
    password: string;
    cost: number;
    hash: string;
    verifyHash: string;
    verifyResult: 'match' | 'mismatch' | null;
    busy: boolean;
    copied: boolean;
}

export default class BcryptGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        password: 'p4ssw0rd!',
        cost: 10,
        hash: '',
        verifyHash: '',
        verifyResult: null,
        busy: false,
        copied: false,
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    generate = async () => {
        this.setState({ busy: true });
        try {
            const hash = await bcrypt.hash(this.state.password, this.state.cost);
            this.setState({ hash, busy: false, copied: false });
        } catch (e: any) {
            this.setState({ hash: 'Error: ' + e.message, busy: false });
        }
    };

    verify = async () => {
        if (!this.state.password || !this.state.verifyHash) return;
        this.setState({ busy: true });
        try {
            const ok = await bcrypt.compare(this.state.password, this.state.verifyHash);
            this.setState({ verifyResult: ok ? 'match' : 'mismatch', busy: false });
        } catch {
            this.setState({ verifyResult: 'mismatch', busy: false });
        }
    };

    copyHash = () => {
        if (!this.state.hash || this.state.hash.startsWith('Error')) return;
        navigator.clipboard.writeText(this.state.hash).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { password, cost, hash, verifyHash, verifyResult, busy, copied } = this.state;
        return (
            <div className="row">
                <div className="col-12 col-md-6">
                    <div className={genStyles.section}>
                        <div className={genStyles.sectionHeader}>
                            <span className={genStyles.sectionTitle}>Generate</span>
                        </div>
                        <label className={sharedStyles.label}>
                            Password
                            <input className={styles.input} type="text" value={password}
                                spellCheck={false} autoComplete="off"
                                onChange={e => this.setState({ password: e.target.value })} />
                        </label>
                        <label className={sharedStyles.label}>
                            Cost factor (rounds = 2^cost)
                            <input className={styles.input} type="number" inputMode="decimal" min={4} max={15} value={cost}
                                onChange={e => this.setState({ cost: Math.max(4, Math.min(15, +e.target.value || 10)) })} />
                        </label>
                        <button className={sharedStyles.buttonPrimary} onClick={this.generate} disabled={busy}>
                            {busy ? 'Hashing…' : 'Hash'}
                        </button>
                        <p className={styles.note}>Cost 10 ≈ 80 ms. Cost 12 ≈ 320 ms. Each +1 doubles the work.</p>

                        <label className={sharedStyles.label} style={{ marginTop: '1rem' }}>Bcrypt hash</label>
                        <textarea className={`${sharedStyles.textarea} ${styles.mono}`} value={hash} readOnly rows={3} />
                        <button className={`${sharedStyles.copyButton} ${copied ? sharedStyles.copyButtonSuccess : ''}`}
                            onClick={this.copyHash} disabled={!hash}>
                            {copied ? '✓ Copied' : 'Copy hash'}
                        </button>
                    </div>
                </div>
                <div className="col-12 col-md-6">
                    <div className={genStyles.section}>
                        <div className={genStyles.sectionHeader}>
                            <span className={genStyles.sectionTitle}>Verify</span>
                        </div>
                        <label className={sharedStyles.label}>
                            Existing hash to verify against
                            <textarea className={`${sharedStyles.textarea} ${styles.mono}`} value={verifyHash} rows={3}
                                spellCheck={false}
                                placeholder="$2a$10$…"
                                onChange={e => this.setState({ verifyHash: e.target.value, verifyResult: null })} />
                        </label>
                        <button className={sharedStyles.buttonPrimary} onClick={this.verify} disabled={busy || !verifyHash}>
                            Verify against password above
                        </button>
                        {verifyResult === 'match' && <p className={styles.matchOk}>✓ Password matches the hash.</p>}
                        {verifyResult === 'mismatch' && <p className={styles.matchFail}>✗ Password does not match.</p>}
                    </div>
                </div>
            </div>
        );
    }
}
